import { useTranslation } from "react-i18next";
import { CountPulse } from "../../vroom/count-pulse.tsx";

export type StatusTab = "todo" | "ok" | "rejected" | "exported";

interface UrenStatusTabsProps {
  value: StatusTab;
  counts: { todo: number; ok: number; rejected: number; exported: number };
  onChange: (v: StatusTab) => void;
  onPrefetch?: (v: StatusTab) => void;
}

export function UrenStatusTabs({ value, counts, onChange, onPrefetch }: UrenStatusTabsProps) {
  const { t } = useTranslation();
  const tabs: { key: StatusTab; label: string; count: number }[] = [
    { key: "todo", label: t("admin.uren.tabs.todo"), count: counts.todo },
    { key: "ok", label: t("admin.uren.tabs.ok"), count: counts.ok },
    { key: "rejected", label: t("admin.uren.tabs.rejected"), count: counts.rejected },
    { key: "exported", label: t("admin.uren.tabs.exported"), count: counts.exported },
  ];
  return (
    <div
      role="tablist"
      className="flex items-center gap-1 overflow-x-auto whitespace-nowrap border-b border-line"
    >
      {tabs.map((tab) => {
        const isAttention = tab.key === "todo" && tab.count > 0;
        const isActive = value === tab.key;
        const tabClasses = `px-3.5 py-2.5 text-[12.5px] ${
          isActive ? "font-semibold" : "font-medium text-ink-700"
        } ${isAttention ? "text-vroom-red" : ""}`;
        const indicatorColor = isActive
          ? isAttention
            ? "border-vroom-red"
            : "border-p-600"
          : "border-transparent";
        const badgeClasses = isActive
          ? isAttention
            ? "bg-vroom-red text-white"
            : "bg-p-600 text-white"
          : isAttention
            ? "bg-vroom-red-soft text-vroom-red border border-vroom-red-edge"
            : "bg-surface-1 text-ink-700 border border-line";
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            onMouseEnter={onPrefetch ? () => onPrefetch(tab.key) : undefined}
            onFocus={onPrefetch ? () => onPrefetch(tab.key) : undefined}
            className={`${tabClasses} border-b-2 ${indicatorColor} inline-flex shrink-0 items-center gap-2`}
          >
            <span>{tab.label}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10.5px] tabular-nums ${badgeClasses}`}>
              <CountPulse value={tab.count} />
            </span>
          </button>
        );
      })}
    </div>
  );
}
